/**
 * Central axios API client with auth + refresh-token handling.
 *
 * Behaviour:
 *   - Request interceptor attaches the current access token.
 *   - On a 401 response, the interceptor calls /api/auth/refresh once,
 *     swaps in the new access token, and retries the original request.
 *   - Concurrent 401s during the same expiry window share a single
 *     in-flight /refresh promise (no thundering-herd on the refresh
 *     endpoint).
 *   - If /refresh itself fails (or no refresh token is stored), the
 *     interceptor performs a hard logout: clears tokens, emits an
 *     auth:logout event so AuthContext can react, and lets the original
 *     request reject so the caller sees the failure.
 *   - The /refresh call deliberately uses a bare axios instance to avoid
 *     re-entering this interceptor.
 */

import axios, {
  AxiosError,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import { emitAuthLogout } from './authEvents';
import { tokenStorage } from './tokenStorage';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? '';

const REFRESH_PATH = '/api/auth/refresh';

// Endpoints that must NEVER trigger a refresh-and-retry. Hitting refresh
// recursively on /login (etc.) would mask credential errors as logouts.
const AUTH_ENDPOINTS_NO_REFRESH = [
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/refresh',
  '/api/auth/logout',
  '/api/auth/azure/login',
  '/api/auth/azure/callback',
];

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStorage.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  return config;
});

// ---------------------------------------------------------------------------
// Refresh coordination
// ---------------------------------------------------------------------------

let refreshInFlight: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  // Bare axios call — must NOT go through `api` (would recurse on 401).
  const response = await axios.post(
    `${API_BASE_URL}${REFRESH_PATH}`,
    { refresh_token: refreshToken },
    {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    },
  );

  const newAccessToken: string | undefined = response.data?.access_token;
  const newRefreshToken: string | undefined = response.data?.refresh_token;
  if (!newAccessToken) {
    throw new Error('Refresh response missing access_token');
  }

  tokenStorage.updateAccessToken(newAccessToken);
  if (newRefreshToken) {
    tokenStorage.setRefreshToken(newRefreshToken);
  }
  return newAccessToken;
}

function getOrStartRefresh(): Promise<string> {
  if (refreshInFlight === null) {
    refreshInFlight = refreshAccessToken().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

function hardLogout(reason: string): void {
  tokenStorage.clearAll();
  emitAuthLogout(reason);
}

function isAuthEndpoint(url?: string): boolean {
  if (!url) return false;
  return AUTH_ENDPOINTS_NO_REFRESH.some((path) => url.includes(path));
}

interface RetryableConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const original = error.config as RetryableConfig | undefined;

    if (status !== 401 || !original) {
      return Promise.reject(error);
    }

    // Don't try to refresh on auth endpoints — a 401 from /login means
    // bad credentials, not an expired session.
    if (isAuthEndpoint(original.url)) {
      return Promise.reject(error);
    }

    // Don't loop: if this request is already a retry, bail out.
    if (original._retry) {
      hardLogout('refresh_retry_failed');
      return Promise.reject(error);
    }

    // No refresh token? Nothing to do but log out.
    if (!tokenStorage.getRefreshToken()) {
      hardLogout('no_refresh_token');
      return Promise.reject(error);
    }

    original._retry = true;

    try {
      const newAccessToken = await getOrStartRefresh();
      original.headers = original.headers ?? {};
      (original.headers as Record<string, string>).Authorization =
        `Bearer ${newAccessToken}`;
      return api.request(original);
    } catch (refreshError) {
      hardLogout('refresh_failed');
      return Promise.reject(refreshError);
    }
  },
);

export default api;

export async function get<T = any>(url: string, params?: any): Promise<T> {
  const response = await api.get<T>(url, { params });
  return response.data;
}

export async function post<T = any>(url: string, data?: any, config?: any): Promise<T> {
  const response = await api.post<T>(url, data, config);
  return response.data;
}

export async function put<T = any>(url: string, data?: any): Promise<T> {
  const response = await api.put<T>(url, data);
  return response.data;
}

export async function del<T = any>(url: string): Promise<T> {
  const response = await api.delete<T>(url);
  return response.data;
}
